import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRequestPasswordReset } from '../hooks/useAuth';
import { ROUTES } from '../../../config/routes';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Alert } from '../../../components/ui/alert';

const forgotPasswordSchema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordForm: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const { mutate: requestReset, isPending } = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    requestReset(data.email, {
      onSuccess: () => setSubmitted(true),
    });
  };

  if (submitted) {
    return (
      <div className="w-full">
        <Alert variant="success" className="mb-6">
          確認コードをメールで送信しました。
          メールが届かない場合は、迷惑メールフォルダをご確認ください。
        </Alert>
        <RouterLink
          to={ROUTES.LOGIN}
          className="flex justify-center items-center mt-4 text-sm text-primary hover:underline"
        >
          ログインに戻る
        </RouterLink>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
      <div className="mb-5">
        <label htmlFor="forgot-email" className="block text-sm font-medium mb-1.5">メールアドレス</label>
        <Input
          id="forgot-email"
          {...register('email')}
          type="email"
          placeholder="yamada@example.co.jp"
          autoComplete="email"
          className="!text-[16px] md:!text-sm"
          aria-invalid={!!errors.email}
          aria-describedby="forgot-email-error"
        />
        {errors.email && (
          <p id="forgot-email-error" className="text-sm text-red-500 mt-1">
            {errors.email.message}
          </p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full !h-auto !py-3.5 !text-base !font-bold !rounded-lg md:!py-3 md:!text-[15px]"
        disabled={isPending}
      >
        {isPending ? '送信中...' : '確認コードを送信'}
      </Button>

      <RouterLink
        to={ROUTES.LOGIN}
        className="flex justify-center items-center mt-6 text-sm text-primary hover:underline"
      >
        ログインに戻る
      </RouterLink>
    </form>
  );
};
